import { DashboardSection } from '@g17eco/molecules/dashboard';
import { UserStatsPermissionView } from '../types';
import { useCallback, useMemo, useState } from 'react';
import { AdminUserTable } from './AdminUserTable';
import { useHistory } from 'react-router-dom';
import { MessageModal } from '@features/message-modal/MessageModal';
import { UserDelegationStats } from '@g17eco/types/initiativeStats';
import { generateUsersFlexSearchMap } from '../utils';
import { DebouncedSearchBox } from '@g17eco/molecules/search';
import { Toggle } from '@g17eco/molecules/toggle';
import { MessageFormProps, MessageType } from '@features/message-modal/types';
import { BulkUsersMessageForm } from '@features/message-modal/partials/BulkUsersMessageForm';
import type { SerializedError } from '@reduxjs/toolkit';
import { useToggle } from '@hooks/useToggle';
import { ASSIGNMENT } from '@constants/terminology';

interface Props {
  initiativeId: string;
  url: string;
  userId: string | null;
  userListStats: UserDelegationStats[];
  error?:
    | {
        message: string;
        name: string;
      }
    | SerializedError
    | undefined;
}

export const UserStatsTable = (props: Props) => {
  const { initiativeId, url, userId, userListStats, error } = props;
  const [view, setView] = useState<UserStatsPermissionView>(UserStatsPermissionView.Explicit);
  const [searchText, setSearchText] = useState<string>('');
  const [showBulkUsersMessage, toggleShowBulkUsersMessage] = useToggle();

  const history = useHistory();

  const isPermissionFilter = view === UserStatsPermissionView.Permission;

  const users: UserDelegationStats[] = useMemo(() => {
    return userListStats.map((user) => ({
      ...user,
      delegationStats: view !== UserStatsPermissionView.Permission ? user.utrvDelegation : user.totalDelegation,
    }));
  }, [userListStats, view]);

  const filteredRows = useMemo(() => {
    if (searchText) {
      const searchIndex = generateUsersFlexSearchMap(users);
      const result = searchIndex.search(searchText);
      const matchedIds = new Set(result.map((item) => item.result).flat());
      return users.filter((user) => matchedIds.has(user._id));
    }
    return users;
  }, [users, searchText]);

  // Stable identity: an inline Component would remount the form (resetting its filter and
  // overwriting selected recipients) whenever this table re-renders while the modal is open
  const bulkMessageForm = useCallback(
    ({ onChange, form }: MessageFormProps) => <BulkUsersMessageForm users={users} onChange={onChange} form={form} />,
    [users],
  );

  const handleToggle = () => {
    const newView = isPermissionFilter ? UserStatsPermissionView.Explicit : UserStatsPermissionView.Permission;
    setView(newView);
  };

  const onSearchChange = (value?: string) => {
    setSearchText(value || '');
  };

  return (
    <>
      <DashboardSection padding={0}>
        <div className='d-flex align-items-center justify-content-between mb-3'>
          <div className='h5'>User progress breakdown</div>
          <Toggle
            className={{ form: 'd-flex align-items-center pl-0 mb-0 justify-content-end' }}
            checked={isPermissionFilter}
            onChange={handleToggle}
            label={`${ASSIGNMENT.roleAccess} users`}
          />
        </div>
        <DebouncedSearchBox
          placeholder='Search...'
          onTextChange={onSearchChange}
          classNames={{ wrapper: 'flex-fill mb-3' }}
        />
        <AdminUserTable
          onUserClick={(user) => {
            history.push(`${url}/users/${user._id}`);
          }}
          initiativeId={initiativeId}
          highlightUserId={userId}
          users={filteredRows}
          error={error}
          toggleShowBulkUsersMessage={toggleShowBulkUsersMessage}
        />
      </DashboardSection>
      {showBulkUsersMessage ? (
        <MessageModal
          users={users}
          toggle={toggleShowBulkUsersMessage}
          type={MessageType.User}
          Component={bulkMessageForm}
        />
      ) : null}
    </>
  );
};
